import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import type { InputProps } from "react-daisyui";
import { twMerge } from "tailwind-merge";

export interface InputTextProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: InputProps["size"];
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  formControlClass?: string;
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      label,
      error,
      helperText,
      required = false,
      size,
      className,
      id,
      endAdornment,
      startAdornment,
      formControlClass,
      children,
      ...props
    },
    ref,
  ) => {
    const inputClasses = twMerge(
      "input input-bordered w-full",
      endAdornment && "pr-12",
      startAdornment && "pl-12",
      error && "input-error",
      size && `input-${size}`,
      className,
    );

    const formControlClasses = twMerge("form-control relative", formControlClass);
    return (
      <label className={formControlClasses} htmlFor={id}>
        {label && (
          <div className="label pt-0">
            <span className="label-text">
              {label}
              {required && "*"}
            </span>
          </div>
        )}
        <div className="relative">
          {startAdornment && (
            <span className="absolute top-1/2 left-4 inline-flex -translate-y-1/2 text-base-300 text-md">
              {startAdornment}
            </span>
          )}
          <input type="text" className={inputClasses} ref={ref} {...props} id={id} />
          {endAdornment && (
            <span className="absolute top-1/2 right-4 inline-flex -translate-y-1/2 text-base-300 text-md">
              {endAdornment}
            </span>
          )}
        </div>
        {helperText && !error && (
          <div className="label">
            <span className="label-text-alt text-zinc-500">{helperText}</span>
          </div>
        )}
        {error && (
          <div className="label absolute -bottom-5 p-0">
            <span className="label-text-alt text-error">{error}</span>
          </div>
        )}
      </label>
    );
  },
);
